import { cn, useTranslation } from '@hbs-us/ui';

import { useMoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { JobProfitabilityDto } from '../contractsMirror.js';

/**
 * The Profitability tab (D-05) — the former standalone job-financials screen FOLDED into Job
 * detail as a B-archetype mini-dashboard.
 *
 * It renders the SERVER-computed `profitability` aggregate VERBATIM. The numbers come from
 * `GET /api/v1/jobs/:id`, where the golden-locked job-profit-calc computes them from CORE-D1
 * inputs ONLY (income / expense / labor / contract / retainage). This component NEVER
 * re-aggregates the figures client-side, and NEVER imports or calls any payments client/service —
 * profit must never aggregate Stripe-money data across that trust boundary (T-22-vault / P18 D-01).
 *
 * Every number renders in IBM Plex Mono (`font-mono`). Profit/margin carry their sign on the
 * number itself, not color alone (A11Y-01). Every color is a locked `@hbs-us/ui` token (no hex).
 */

function StatCard({
  label,
  value,
  sub,
  valueClassName,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </div>
      <div className={cn('mt-2 font-mono font-semibold text-foreground text-xl', valueClassName)}>
        {value}
      </div>
      {sub ? <div className="mt-1 text-muted-foreground text-xs">{sub}</div> : null}
    </div>
  );
}

export function ProfitabilityTab({ profit }: { profit: JobProfitabilityDto }) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();

  const isProfit = profit.profit >= 0;

  // Cost buckets that make up totalCosts — materials/other expenses vs labor (the same split the
  // legacy job-financials screen showed). These are server values; nothing is re-summed here.
  const costItems = [
    { key: 'expenses', value: profit.totalExpenses },
    { key: 'labor', value: profit.totalLabor },
  ] as const;

  return (
    <div className="space-y-6">
      {/* The P&L stat grid: Revenue · Total Costs · Net Profit · Retainage Held. */}
      <section
        aria-label={t('jobs:profitability.summaryLabel')}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatCard
          label={t('jobs:profitability.revenue')}
          value={fmt.formatCurrency(profit.totalIncome)}
          sub={t('jobs:profitability.revenueSub', {
            amount: fmt.formatCurrency(profit.contractAmount),
          })}
        />
        <StatCard
          label={t('jobs:profitability.totalCosts')}
          value={fmt.formatCurrency(profit.totalCosts)}
          sub={t('jobs:profitability.totalCostsSub')}
        />
        <StatCard
          label={t('jobs:profitability.netProfit')}
          value={fmt.formatCurrency(profit.profit)}
          valueClassName={isProfit ? 'text-success' : 'text-destructive'}
          sub={t('jobs:profitability.netProfitSub', { percent: fmt.percent(profit.profitMargin) })}
        />
        <StatCard
          label={t('jobs:profitability.retainageHeld')}
          value={fmt.formatCurrency(profit.retainageHeld)}
          sub={t('jobs:profitability.retainageHeldSub', {
            percent: fmt.percent(profit.retainagePercent),
          })}
        />
      </section>

      {/* Cost breakdown — materials/other expenses vs labor (the two cost inputs to totalCosts). */}
      <section className="rounded-md border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold text-foreground text-lg">
          {t('jobs:profitability.costsHeading')}
        </h3>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4">
          {costItems.map((item) => (
            <div key={item.key} className="rounded-md border border-border bg-background p-4">
              <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                {t(`jobs:profitability.cost.${item.key}`)}
              </div>
              <div className="mt-2 font-mono font-semibold text-foreground text-lg">
                {fmt.formatCurrency(item.value)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The detail breakdown table: each profitability line + its server value. */}
      <section className="rounded-md border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold text-foreground text-lg">
          {t('jobs:profitability.breakdownHeading')}
        </h3>
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{t('jobs:profitability.breakdownCaption')}</caption>
          <tbody>
            <BreakdownRow
              label={t('jobs:profitability.revenue')}
              value={fmt.formatCurrency(profit.totalIncome)}
            />
            <BreakdownRow
              label={t('jobs:profitability.cost.expenses')}
              value={fmt.formatCurrency(profit.totalExpenses)}
            />
            <BreakdownRow
              label={t('jobs:profitability.cost.labor')}
              value={fmt.formatCurrency(profit.totalLabor)}
            />
            <BreakdownRow label={t('jobs:profitability.hours')} value={String(profit.totalHours)} />
            <BreakdownRow
              label={t('jobs:profitability.netProfit')}
              value={fmt.formatCurrency(profit.profit)}
              valueClassName={isProfit ? 'text-success' : 'text-destructive'}
            />
            <BreakdownRow
              label={t('jobs:profitability.remainingContract')}
              value={fmt.formatCurrency(profit.remainingContract)}
            />
          </tbody>
        </table>
      </section>
    </div>
  );
}

function BreakdownRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <tr className="border-border/60 border-b last:border-b-0">
      <th scope="row" className="py-2 pr-4 text-left font-medium text-muted-foreground">
        {label}
      </th>
      <td className={cn('py-2 text-right font-mono font-semibold text-foreground', valueClassName)}>
        {value}
      </td>
    </tr>
  );
}
